import { createSelector } from '@reduxjs/toolkit';
import type { State } from '../types';
import { getOrganization } from './organization/getOrganization';
import type { TutorialContent } from 'src/outskirts/tutorial-content';
import type { TutorialTemplatePart } from '@trinnvis/dabih-api-client';

/**
 * Find current participant and registration based on currentTutorialParticipantId
 */
function _findParticipantAndRegistration(state: State) {
  const participantId = state.currentTutorialParticipantId;
  if (!participantId) return undefined;

  const organization = getOrganization(state);
  if (!organization?.tutorials) return undefined;

  for (const tutorial of organization.tutorials) {
    if (!tutorial.registrations) continue;

    for (const registration of tutorial.registrations) {
      if (!registration.participants) continue;

      for (const participant of registration.participants) {
        if (participant.id === participantId) {
          return {
            tutorial,
            registration,
            participant,
          };
        }
      }
    }
  }

  return undefined;
}

/**
 * Selector for current tutorial content
 * Returns TutorialContent merged with participant state (currentPart, partStates, rating)
 * Content is cached based on tutorialId-versionId key
 * Participant state is fetched separately and stored in currentTutorialParticipantState
 */
export const currentTutorialContent = createSelector(
  [
    (state: State) => state.currentTutorialParticipantId,
    (state: State) => state.currentTutorialParticipantState,
    (state: State) => state.tutorialContentCache,
    (state: State) => getOrganization(state),
  ],
  (participantId, participantState, contentCache, organization): TutorialContent | undefined => {
    console.log('[currentTutorialContent] Selector called', {
      participantId,
      hasParticipantState: !!participantState,
      participantState,
      contentCacheKeys: Object.keys(contentCache),
      hasOrganization: !!organization,
    });

    if (!participantId || !organization) {
      console.log('[currentTutorialContent] Missing participantId or organization');
      return undefined;
    }

    // Find participant, registration, and tutorial
    const found = _findParticipantAndRegistration({
      currentTutorialParticipantId: participantId,
      organization,
    } as State);
    if (!found) {
      console.log('[currentTutorialContent] Participant not found in registrations');
      return undefined;
    }

    const { tutorial, registration } = found;
    const cacheKey = `${tutorial.id}-${registration.contentVersionId}`;
    console.log('[currentTutorialContent] Cache key:', cacheKey);

    // Get cached template content
    const templateContent = contentCache[cacheKey];
    if (!templateContent) {
      console.log('[currentTutorialContent] Template content not in cache');
      return undefined;
    }

    console.log('[currentTutorialContent] Template content FROM CACHE:', {
      name: templateContent.name,
      description: templateContent.description,
      intro: templateContent.intro,
      outro: templateContent.outro,
      partsCount: templateContent.parts?.length,
      // Check what fields each part has
      partFields: templateContent.parts?.map((p) => Object.keys(p)),
      // Detailed test info for each part
      partsTestInfo: templateContent.parts?.map((p, i) => ({
        index: i,
        title: p.title,
        hasTestField: 'test' in p,
        testValue: p.test,
        testLength: p.test?.length || 0,
      })),
      // Check if any part has test data
      hasTests: templateContent.parts?.some((p: TutorialTemplatePart) => p.test && p.test.length > 0),
      // Raw parts for inspection
      rawParts: templateContent.parts,
    });

    // If participant state not loaded yet, return undefined
    if (!participantState) {
      console.log('[currentTutorialContent] Participant state not loaded yet');
      return undefined;
    }

    // Merge template content with participant state
    const result = {
      id: tutorial.id.toString(),
      title: templateContent.name,
      description: templateContent.description,
      intro: templateContent.intro, // ✅ Now comes from backend
      outro: templateContent.outro, // ✅ Now comes from backend
      parts: templateContent.parts.map((part: TutorialTemplatePart, index) => {
        const mappedTests =
          part.test?.map((q) => ({
            type: q.type,
            label: q.label,
            content: q.content,
            correct: q.correct || [],
            options: q.options || [],
          })) || [];

        console.log(`[currentTutorialContent] Part ${index} (${part.title}):`, {
          hasTestField: !!part.test,
          testCount: part.test?.length || 0,
          rawTests: part.test,
          mappedTests,
        });

        return {
          title: part.title,
          content: part.content,
          state: participantState.partStates?.[index] || '',
          test: mappedTests,
        };
      }),
      currentPart: participantState.currentPart || 0,
      rate: tutorial.rate || false,
      rating: participantState.rating || 0,
      guide: tutorial.guide || false,
    };

    console.log('[currentTutorialContent] Returning merged content:', {
      id: result.id,
      title: result.title,
      partsCount: result.parts.length,
      currentPart: result.currentPart,
      parts: result.parts,
      // Check how many tests each part has
      testsPerPart: result.parts.map((p) => p.test?.length || 0),
    });

    return result;
  },
);
